import React, { useEffect, useState } from 'react'
import {
	Box,
	Dialog,
	IconButton,
	MenuItem,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import { type GridColDef } from '@mui/x-data-grid-pro'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { type Dayjs } from 'dayjs'
import { useModal } from '../../../../../store/ModalProvider'
import CustomButton from '../../CustomButton/CustomButton'
import { AnimatedDataGridPro, type DensityMode } from '../../CustomDataGrid'
import { dataGridDensityTransitionSx } from '../../CustomDataGrid/dataGridDensityTransitionSx'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import { calendarInputSx, calendarPopperSlotProps, popupInputLabelSx } from '../popupInputStyles'
import { popupActionButtonSx, popupSecondaryButtonSx } from '../popupButtonStyles'
import type { VehicleSituationModalRow, VehicleSituationsModalData } from '../../../../../types/modal'

const dialogShadow =
	'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)'

const parseDateValue = (value?: string): Dayjs | null => {
	if (!value) {
		return null
	}

	const [day, month, year] = value.split('/')
	if (!day || !month || !year) {
		return null
	}

	const parsed = dayjs(`${year}-${month}-${day}`)
	return parsed.isValid() ? parsed : null
}

// Invisible but clickable opener so picker still opens when clicking input area.
const EmptyButton: React.ComponentType<any> = (props: any) => (
	<IconButton
		{...props}
		sx={{ ...(props?.sx || {}), opacity: 0 }}
	/>
)

const inputSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#FFFFFF',
		borderRadius: '4px',
		minHeight: '40px',
		fontSize: '13px',
		fontWeight: 500,
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.4)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.6)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '8px 12px',
		lineHeight: 1.3,
	},
	'& .MuiSelect-icon': {
		color: '#00A5FF',
	},
}

const toggleGroupSx = {
	border: '1px solid rgba(134, 149, 175, 0.15)',
	borderRadius: '4px',
	'& .MuiToggleButton-root': {
		border: 'none',
		borderRadius: 0,
		padding: '4.8px',
		'&:not(:last-child)': {
			borderRight: '0.6px solid rgba(134, 149, 175, 0.15)',
		},
		'&.Mui-selected': {
			backgroundColor: '#F5F7FC',
			'&:hover': {
				backgroundColor: '#F5F7FC',
			},
		},
	},
}

const tableSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		fontFamily: 'Poppins, sans-serif',
		fontWeight: 600,
		fontSize: '12px',
		color: '#8695AF',
		letterSpacing: '0.17px',
		lineHeight: '24px',
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
	},
	'& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '12px',
		lineHeight: 1.3,
		color: '#092056',
		borderBottom: 'none',
		whiteSpace: 'nowrap',
	},
	'& .MuiDataGrid-row': {
		borderBottom: 'none',
	},
	'& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox': {
		paddingLeft: '8px',
		paddingRight: '8px',
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
}

const defaultRows: VehicleSituationModalRow[] = [
	{
		id: '1',
		inicio: 'sex 10/05/2024',
		fim: 'sex 10/05/2024',
		situacao: 'Avariada 1/2 dia',
		descricao: 'Portas passageiros não funcionam',
	},
	{
		id: '2',
		inicio: 'sex 10/05/2024',
		fim: 'sex 10/05/2024',
		situacao: 'Avariada 1/2 dia',
		descricao: 'Portas passageiros não funcionam',
	},
	{
		id: '3',
		inicio: 'sex 10/05/2024',
		fim: 'sex 10/05/2024',
		situacao: 'Avariada 1/2 dia',
		descricao: 'Portas passageiros não funcionam',
	},
]

const columns: GridColDef<VehicleSituationModalRow>[] = [
	{ field: 'inicio', headerName: 'Início', minWidth: 140, flex: 1 },
	{ field: 'fim', headerName: 'Fim', minWidth: 140, flex: 1 },
	{ field: 'situacao', headerName: 'Situação', minWidth: 140, flex: 1 },
	{ field: 'descricao', headerName: 'Descrição', minWidth: 220, flex: 2 },
]

const CustomModalVehicleSituations: React.FC = () => {
	const { isOpen, closeModal, modalData, modalHandlers } = useModal()
	const data = modalData as VehicleSituationsModalData | undefined
	const [handlePrint, handleSend] = modalHandlers ?? []

	const [density, setDensity] = useState<DensityMode>('compact')
	const [selectedRows, setSelectedRows] = useState<Array<string | number>>([])
	const [rows, setRows] = useState<VehicleSituationModalRow[]>(data?.rows?.length ? data.rows : defaultRows)
	const [startDate, setStartDate] = useState<Dayjs | null>(parseDateValue(data?.startDate ?? '01/05/2024'))
	const [endDate, setEndDate] = useState<Dayjs | null>(parseDateValue(data?.endDate ?? '01/09/2024'))
	const [situationFilter, setSituationFilter] = useState(data?.situacao ?? 'Todas')

	useEffect(() => {
		setRows(data?.rows?.length ? data.rows : defaultRows)
		setStartDate(parseDateValue(data?.startDate ?? '01/05/2024'))
		setEndDate(parseDateValue(data?.endDate ?? '01/09/2024'))
		setSituationFilter(data?.situacao ?? 'Todas')
		setSelectedRows([])
	}, [data])

	const handleClearFilters = () => {
		setStartDate(parseDateValue('01/05/2024'))
		setEndDate(parseDateValue('01/09/2024'))
		setSituationFilter('Todas')
	}

	const handlePrintSituations = () => {
		if (typeof handlePrint === 'function') {
			handlePrint(rows)
			return
		}
		console.log('Imprimir situações da viatura', rows)
	}

	const handleSendSituations = () => {
		if (typeof handleSend === 'function') {
			handleSend(rows)
			return
		}
		console.log('Enviar situações da viatura', rows)
	}

	return (
		<Dialog
			open={isOpen}
			onClose={closeModal}
			maxWidth={false}
			sx={{ pointerEvents: 'auto', zIndex: 2000 }}
			PaperProps={{
				sx: {
					width: 'min(1200px, 96vw)',
					borderRadius: '6px',
					backgroundColor: '#FFFFFF',
					boxShadow: dialogShadow,
					overflow: 'hidden',
					zIndex: 2001,
				},
			}}
		>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<Box sx={{ padding: '16px 24px' }}>
						<Typography
							sx={{
								fontFamily: 'Poppins, sans-serif',
								fontWeight: 600,
								fontSize: '20px',
								lineHeight: 1.3,
								color: '#092056',
							}}
						>
							Situações da Viatura
						</Typography>
					</Box>

					<Box sx={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								alignItems: 'center',
								gap: '16px',
							}}
						>
							<DatePicker
								label='Data Inicial'
								value={startDate}
								onChange={value => setStartDate(value)}
								format='DD/MM/YYYY'
								slotProps={{
									textField: {
										size: 'small',
										sx: [inputSx, calendarInputSx, { minWidth: '164px' }],
									},
									...calendarPopperSlotProps,
								}}
								slots={{ openPickerButton: EmptyButton }}
							/>
							<Typography
								sx={{
									fontFamily: 'Poppins, sans-serif',
									fontSize: '13px',
									fontWeight: 500,
									lineHeight: 1.3,
									color: '#092056',
									alignSelf: 'center',
								}}
							>
								{String.fromCharCode(0x2014)}
							</Typography>
							<DatePicker
								label='Data Final'
								value={endDate}
								onChange={value => setEndDate(value)}
								format='DD/MM/YYYY'
								slotProps={{
									textField: {
										size: 'small',
										sx: [inputSx, calendarInputSx, { minWidth: '164px' }],
									},
									...calendarPopperSlotProps,
								}}
								slots={{ openPickerButton: EmptyButton }}
							/>
							<TextField
								select
								label='Situação'
								size='small'
								value={situationFilter}
								onChange={event => setSituationFilter(event.target.value)}
								SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
								sx={{ ...inputSx, minWidth: '187px' }}
							>
								<MenuItem value='Todas'>Todas</MenuItem>
								<MenuItem value='Avariada 1/2 dia'>Avariada 1/2 dia</MenuItem>
								<MenuItem value='Em Oficina'>Em Oficina</MenuItem>
							</TextField>

							<CustomButton
								variant='text'
								size='small'
								onClick={handleClearFilters}
								sx={{
									color: '#00A5FF',
									fontWeight: 600,
									fontSize: '14px',
									lineHeight: '22px',
									textTransform: 'none',
									marginLeft: 'auto',
									padding: '4px 0',
								}}
							>
								Limpar Filtros
							</CustomButton>
						</Box>

						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								flexWrap: 'wrap',
								gap: '16px',
							}}
						>
							<Typography
								sx={{
									fontFamily: 'Poppins, sans-serif',
									fontWeight: 600,
									fontSize: '24px',
									lineHeight: 1.334,
									color: '#092056',
								}}
							>
								{rows.length} Situações
							</Typography>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
								<CustomButton
									variant='text'
									size='small'
									sx={{
										color: '#00A5FF',
										textTransform: 'none',
										padding: '4px 0',
										fontWeight: 600,
										fontSize: '14px',
										lineHeight: '22px',
									}}
									endIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
								>
									Criar Situação
								</CustomButton>
								<ToggleButtonGroup
									value={density}
									exclusive
									onChange={(_, nextDensity) => nextDensity && setDensity(nextDensity)}
									size='small'
									sx={toggleGroupSx}
								>
									<ToggleButton
										value='compact'
										aria-label='densidade compacta'
									>
										<TableRowsIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: density === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
									<ToggleButton
										value='standard'
										aria-label='densidade espaçada'
									>
										<ViewHeadlineIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: density === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>
						</Box>

						<AnimatedDataGridPro
							slots={{ columnMenu: CustomColumnMenu }}
							slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
							rows={rows}
							columns={columns}
							density={density}
							rowHeight={density === 'compact' ? 32 : 40}
							columnHeaderHeight={32}
							autoHeight
							checkboxSelection
							disableRowSelectionOnClick
							hideFooter
							rowSelectionModel={selectedRows}
							onRowSelectionModelChange={selectionModel => setSelectedRows([...selectionModel])}
							sx={[tableSx, dataGridDensityTransitionSx]}
						/>
					</Box>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							gap: '8px',
							padding: '8px',
						}}
					>
						<CustomButton
							variant='text'
							size='small'
							onClick={closeModal}
							sx={popupSecondaryButtonSx}
						>
							Fechar
						</CustomButton>
						<CustomButton
							variant='text'
							size='small'
							onClick={handlePrintSituations}
							sx={popupSecondaryButtonSx}
						>
							Imprimir
						</CustomButton>
						<CustomButton
							size='small'
							onClick={handleSendSituations}
							sx={popupActionButtonSx}
						>
							Enviar
						</CustomButton>
					</Box>
				</Box>
			</LocalizationProvider>
		</Dialog>
	)
}

export default CustomModalVehicleSituations
